import axiosInstance from '../config/axios.config';

/**
 * Support Ticket API client.
 *
 * Maps 1:1 to the PR 4 backend routes registered under /api/support/* in
 * routes/tenant.php. All methods are async + return the parsed `data` from
 * the response wrapper {success, data, pagination?}.
 *
 * Error handling: lets axios errors propagate. Callers should try/catch
 * and use error.response?.data?.message for the server-side message.
 */
class SupportTicketService {
  // ── Lookups (chat picker data sources) ─────────────────────────────

  /** GET /api/support/categories — chat-picker tree */
  async getCategories() {
    const res = await axiosInstance.get('/support/categories');
    return res.data?.data || [];
  }

  /** GET /api/support/context/leads?q=... */
  async searchLeads(q = '') {
    const res = await axiosInstance.get('/support/context/leads', { params: { q } });
    return res.data?.data || [];
  }

  /** GET /api/support/context/banks?q=... */
  async searchBanks(q = '') {
    const res = await axiosInstance.get('/support/context/banks', { params: { q } });
    return res.data?.data || [];
  }

  /** GET /api/support/context/report-types — static catalog */
  async getReportTypes() {
    const res = await axiosInstance.get('/support/context/report-types');
    return res.data?.data || [];
  }

  // ── Ticket lifecycle ──────────────────────────────────────────────

  /**
   * POST /api/support/tickets
   *
   * @param {object} payload
   * @param {string} payload.category
   * @param {string=} payload.subcategory
   * @param {number=} payload.context_lead_id
   * @param {string=} payload.context_bank_id   - UUID
   * @param {object=} payload.context_payload
   * @param {string=} payload.note
   * @param {string=} payload.priority
   */
  async createTicket(payload) {
    const res = await axiosInstance.post('/support/tickets', payload);
    return res.data?.data;
  }

  /**
   * GET /api/support/tickets — paginated list.
   *
   * Reporters get their own tickets only. Admins get the full tenant inbox
   * (the server-side controller handles the filtering).
   */
  async listTickets(params = {}) {
    const res = await axiosInstance.get('/support/tickets', { params });
    return {
      data: res.data?.data || [],
      pagination: res.data?.pagination,
    };
  }

  /** GET /api/support/tickets/{ref} — full thread */
  async getTicket(ref) {
    const res = await axiosInstance.get(`/support/tickets/${encodeURIComponent(ref)}`);
    return res.data?.data;
  }

  /**
   * POST /api/support/tickets/{ref}/replies
   *
   * @param {string} ref
   * @param {object} payload  - { message, is_internal? }
   */
  async addReply(ref, payload) {
    const res = await axiosInstance.post(
      `/support/tickets/${encodeURIComponent(ref)}/replies`,
      payload,
    );
    return res.data?.data;
  }

  /** POST /api/support/tickets/{ref}/status — admin-only */
  async transitionStatus(ref, status) {
    const res = await axiosInstance.post(
      `/support/tickets/${encodeURIComponent(ref)}/status`,
      { status },
    );
    return res.data?.data;
  }

  /**
   * POST /api/support/tickets/{ref}/attachments — multipart upload.
   *
   * @param {string} ref
   * @param {File} file
   * @param {number=} replyId  - if attaching to a specific reply
   */
  async uploadAttachment(ref, file, replyId = null) {
    const fd = new FormData();
    fd.append('file', file);
    if (replyId) fd.append('reply_id', String(replyId));

    const res = await axiosInstance.post(
      `/support/tickets/${encodeURIComponent(ref)}/attachments`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return res.data?.data;
  }
}

const supportTicketService = new SupportTicketService();
export default supportTicketService;
